import ReactGA from 'react-ga4';

// TODO: once this gets big enough, break into a utils folder with many files.
// but dont do this until we have created many established groups/patterns of functions.

import dayjs from 'dayjs';
import { Address } from 'viem';
import {
  BANNER_EXPIRATTION_PREFIX,
  DISMISSED_BANNER_PREFIX,
  MS_IN_SECOND,
  SECONDS_IN_HOUR,
  TVL_TIERS,
} from './constants/shared';

// Maybe codebases break this file apart too early and end up with very confusing and
// poorly divided util files.

// NOTE: please add a docstring to describe each util function if it is not immediately obvious
// why is exists as well as what it does.

export const regularCaseToSkewerCase = (str: string) => {
  return str
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Add a hyphen between lowercase and uppercase letters
    .toLowerCase(); // Convert everything to lowercase
};

export const capitalizeWords = (str: string) => str.replace(/\b\w/g, (c) => c.toUpperCase());

export const getIconPathFromAppId = (id: string) => {
  return `/assets/apps/${regularCaseToSkewerCase(id)}.avif`;
};
export const getBannerImgPathFromAppId = (id: string) => {
  return `/assets/apps/banners/${regularCaseToSkewerCase(id)}-banner.avif`;
};

export const getIconPathFromBadgeId = (id: string, earned = false) => {
  return `/assets/badges/${earned ? '' : 'dis_'}${id}.avif`;
};

export const getIconPathFromSkySocietyId = (id: string) => {
  return `/assets/skysociety/${id}.avif`;
};

export const getIconPathFromTokenSymbol = (symbol: string) => {
  return `/assets/tokens/${symbol.toLowerCase()}.svg`;
};

export const getImgPathFromPrizeId = (
  prizeId: number,
  type: 'withQuantity' | 'plain' | 'disabled' = 'withQuantity',
) => {
  const contractAddressMap: { [k: Address]: string } = {
    // we use the internal values for the test raffle contract
    // the test contract doesnt have any actual prizes so its images dont matter
    '0xEeb52E1052EA69BFD99D90ea40555066fBf1d69D': 'internal', // test raffle contract address
    '0x36Ec4d4eCFA1E40C055Ad4C7CF9085057971aE68': 'mainnet', // mainnet raffle address
    '0x9A292F62546FE80bF9fe0312E5BefD10eeb2cb53': 'internal', // plume internal raffle
  };
  const suffixMap = {
    withQuantity: '',
    plain: '-plain',
    disabled: '-dis',
  };
  const prefixPath = contractAddressMap[import.meta.env.VITE_RAFFLE_CONTRACT_ADDRESS];
  return `/assets/daily-spin/raffle-prizes/${prefixPath}/${prizeId}${suffixMap[type] ?? ''}.avif`;
};

export const capitalizeFirstLetter = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const cardinalIntToOrdinal = (n: number) => {
  if (n < 1) return n.toString(); // Handle non-positive numbers? Not sure we want to do this

  const suffixes: { [key: number]: string } = { 1: 'st', 2: 'nd', 3: 'rd' };
  const lastTwoDigits = n % 100;
  const lastDigit = n % 10;

  // Special case for 11-13 (e.g., 11th, 12th, 13th)
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${n}th`;
  }

  return `${n}${suffixes[lastDigit] || 'th'}`;
};

export const trimSlashes = (str: string) => str.replace(/^\/+|\/+$/g, '');

// function to format a number to display in the local currency with commas
export const formatNumberToLocale = (num: number) => {
  if (num === undefined || num === null) return '';
  return num.toLocaleString();
};

// function to format a number to display in the local currency with commas
export const formatCurrencyToLocale = (num: number) => {
  return (num || 0)?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export const toFixedDecimals = (num: string | number, fixed: number = 2) => {
  const numStr = num.toString();
  const re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?');
  return numStr?.match(re)?.[0] ?? '';
};

export const truncateAddress = (address: string, shorter = false) => {
  if (address.length <= 10) return address; // If the address is too short, don't truncate
  const start = address.slice(0, shorter ? 4 : 6); // First 6 characters
  const end = address.slice(-(shorter ? 3 : 4)); // Last 4 characters
  return `${start}...${end}`;
};

export const getNextDayMidnightUTC = (now: Date) => {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
};

export const getMidnightUTC = (now: Date) => {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

export const occurredToday = (date: Date) => {
  const now = new Date();
  // Get the start of today in UTC (00:00:00 UTC)
  const startOfTodayUTC = getMidnightUTC(now);
  // Get the start of tomorrow in UTC
  const startOfTomorrowUTC = getNextDayMidnightUTC(now);

  return date >= startOfTodayUTC && date < startOfTomorrowUTC;
};

export const NHoursInFutureTimestamp = (n: number) => {
  const now = Date.now();
  return now + n * SECONDS_IN_HOUR * MS_IN_SECOND;
};

export const sleep = (ms: number = 1000): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const extractPositiveNumber = (text: string) => {
  const match = text.match(/\d+(\.\d+)?/);
  return match ? match[0] : '';
};

export const camelToSnakeCase = (str: string): string => {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
};

export const getBlockExplorerUrlFromTxHash = (txHash: string | undefined) => {
  if (!txHash) return;
  return `https://explorer.plume.org/tx/${txHash}`;
};

export const getDismissedBannerKeyFromBannerId = (bannerId: string) => `${DISMISSED_BANNER_PREFIX}_${bannerId}`;

export const getBannerExpirationById = (bannerId: string) => `${BANNER_EXPIRATTION_PREFIX}_${bannerId}`;

export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  ReactGA.event({
    category,
    action,
    label,
    value,
  });
};

export const trackUserLogin = (userId: string) => {
  ReactGA.set({ userId });
};

export const formatListWithAnd = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};
export const formatListWithOr = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}`;
};

export const isTruthy = <T>(value: T | undefined | null | false): value is T => {
  return value !== undefined && value !== null && !!value;
};

// TODO: maybe turn this into a pure number fn. it's kind of confusing to have a magic string for the return to know when something's unlocked
/**
 * Get human readable time until unlock
 * @param unlockTimestamp Unix timestamp in seconds (not milliseconds)
 */
export const getTimeUntilUnlock = (unlockTimestamp: number): string => {
  const now = dayjs();
  const unlockTime = dayjs.unix(unlockTimestamp); // `unlockTimestamp` is in seconds

  if (unlockTime.isBefore(now)) return 'now';

  const diffInMinutes = unlockTime.diff(now, 'minute');
  const diffInHours = unlockTime.diff(now, 'hour');
  const diffInDays = unlockTime.diff(now, 'day');

  if (diffInDays >= 1) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''}`;
  }

  if (diffInHours >= 1) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''}`;
  }

  if (diffInMinutes >= 1) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`;
  }

  return '1 minute';
};

export const truncateWalletAddress = (address: string | undefined) => {
  if (!address || address.length < 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const lowercaseKeys = <T>(obj: T): T | Record<string, unknown> => {
  if (Array.isArray(obj)) {
    return obj.map(lowercaseKeys) as T;
  }

  if (obj && typeof obj === 'object') {
    const entries = Object.entries(obj as Record<string, unknown>);
    const lowered = Object.fromEntries(
      entries.map(([key, value]) => {
        const loweredKey = key.toLowerCase();
        const loweredValue = lowercaseKeys(value);
        return [loweredKey, loweredValue] as const;
      }),
    );
    return lowered;
  }

  return obj;
};
export const lowercaseAddressKeys = <T>(obj: T): T | Record<string, unknown> => {
  if (Array.isArray(obj)) {
    return obj.map(lowercaseAddressKeys) as T;
  }

  if (obj && typeof obj === 'object') {
    const entries = Object.entries(obj as Record<string, unknown>);
    const lowered = Object.fromEntries(
      entries.map(([key, value]) => {
        const isAddress = /^0x[a-fA-F0-9]{40}$/.test(key);
        const loweredKey = isAddress ? key.toLowerCase() : key;
        const loweredValue = lowercaseAddressKeys(value);
        return [loweredKey, loweredValue] as const;
      }),
    );
    return lowered;
  }

  return obj;
};

/**
 * This is used in the Sentry.init to filter axios errors
 *
 * @param err Sentry Error
 * @returns Whether error has a similar structure to an axios error
 */
export const isAxiosLike = (err: unknown): err is { response?: { status: number }; config?: { url?: string } } =>
  !!err && typeof err === 'object' && 'response' in err && 'config' in err;

export const wrapMetamaskError = (err: unknown): Error => {
  const isObject = typeof err === 'object' && err !== null;

  const message = isObject && 'message' in err ? String(err.message) : 'Unknown error';
  const wrapped = new Error(message);
  if (isObject && 'code' in err) {
    wrapped.name = String(err.code);
  } else {
    wrapped.name = 'UnknownError';
  }

  if (isObject && 'stack' in err) {
    wrapped.stack = String(err.stack);
  }

  return wrapped;
};

export const getHighestTvlTier = (tvl: number) => {
  const earnedTvlTiers = TVL_TIERS.filter((tier) => tier <= tvl);
  if (!earnedTvlTiers.length) return 0;
  return Math.max(...earnedTvlTiers);
};

/**
 * TODO: this is probably too opinionated to be reusable, we may need to rewrite this
 * But for now this is fine because extracting it allows it to be well tested
 *
 * Based on an array of values and a focus index, calculates opacity score based on distance to the focus index
 * This requires some math because the focus index could be at the edge of the array, or any point in between
 * And we want the opacity level to gradually increase based on the furthest distance away a point could from
 * the focus index.
 *
 * e.g.
 * if the focus is at the edge of an array of size 10, the opacity should gradually decrease to .2 over 10 indexes
 * but if the focus is in the middle of an array of size 5, the opacity should decrease to .2 over 2 indexes, such
 * that the fade is equivalent on both sides
 */
export const calculateFadeFromFocus = (array: Array<number>, focusIdx: number) => {
  // max distance from the focusIdx is distance to either start or end of the array
  const maxDistance = Math.max(focusIdx, array.length - 1 - focusIdx);
  const visibleTvlTiersWithOpacity = array.map((value, idx) => {
    const distance = Math.abs(focusIdx - idx);
    // if the user is as far as possible from the focus idx, opacity is .2
    // otherwise, scale opacity based on how far the tier is away, e.g:
    // 1 - (2/3)(4/5) =   if the tier is 2 places away when max distance is 3.
    // 1 - (1/2)
    const opacity = maxDistance === 0 ? 1 : 1 - (distance / maxDistance) * 0.8;
    return {
      value,
      opacity,
    };
  });
  return visibleTvlTiersWithOpacity;
};

export const parseAddressFromDID = (did: string) => {
  const parts = did.split(':');
  // DID format: did:pkh:eip155:chainId:address
  return parts[parts.length - 1];
};
